import { createContext, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { getDataFromTaxSysApi } from "../api/taxsys-func-app-service";
import { getDataFromCmsApi } from "../api/contentful-service";

export const NotificationsContext = createContext();

export const NotificationsProvider = ({ children }) => {
    const [notifications, setNotifications] = useState({
        gsg: [],
        global: [],
        division: []
    });

    const [dismissed, setDismissed] = useState({});

    // Fetch GSG/TaxSys banners
    const fetchGSGBanners = async () => {
        try {
            const result = await getDataFromTaxSysApi();

            const bannersBySite = result?.reduce((acc, banner) => {
                if (typeof banner.clientSites === "string") {
                    const clientSitePairs = banner.clientSites.split(",").map(entry => entry.trim());

                    clientSitePairs.forEach(entry => {
                        const parts = entry.split(":");
                        if (parts.length === 2) {
                            const siteName = parts[1];
                            if (!acc[siteName]) {
                                acc[siteName] = [];
                            }
                            acc[siteName].push(banner);
                        }
                    });
                }
                return acc;
            }, {});
            setNotifications(prev => ({ ...prev, gsg: bannersBySite }));
        } catch (error) {
            console.error("Error fetching GSG banners:", error);
        }
    };

    // Fetch CMS global alerts
    const fetchGlobalAlerts = async () => {
        try {
            const result = await getDataFromCmsApi("/Alert/GLOBAL");
            setNotifications(prev => ({ ...prev, global: result }));
        } catch (error) {
            console.error("Error fetching global alerts:", error);
        }
    };

    // Fetch CMS division-specific alerts
    const fetchDivisionAlerts = async () => {
        try {
            const result = await getDataFromCmsApi("/Alert");

            const alertsByDivision = result.reduce((acc, alert) => {
                alert.division.forEach(({ divisionAbbreviation }) => {
                    if (divisionAbbreviation === "GLOBAL") {
                        return;
                    }

                    if (!acc[divisionAbbreviation]) {
                        acc[divisionAbbreviation] = [];
                    }
                    acc[divisionAbbreviation].push(alert);
                });
                return acc;
            }, {});

            setNotifications(prev => ({ ...prev, division: alertsByDivision }));
        } catch (error) {
            console.error(`Error fetching division alerts:`, error);
        }
    };

    const loadDismissed = () => {
        const savedDismissed = JSON.parse(localStorage.getItem("dismissedNotifications")) || {};
        // console.log("Loaded from Dismissed: ", savedDismissed);

        const now = new Date().getTime();
        const validDismissed = Object.entries(savedDismissed).reduce((acc, [id, timestamp]) => {
            if (now - timestamp < 24 * 60 * 60 * 1000) acc[id] = timestamp; // STORE FOR 24 HOURS
            // if (now - timestamp < 60 * 1000) acc[id] = timestamp; // FIX ME: 1 minute for testing
            return acc;
        }, {});

        setDismissed(validDismissed);
        localStorage.setItem("dismissedNotifications", JSON.stringify(validDismissed));
    };

    const generateNotificationKeyCMS = notification => {
        return `${notification.title}-${notification.description}-${notification.division}-${notification.alertTypeCode}`;
    };

    const generateNotificationKeyGSG = notification => {
        return notification.id;
    };

    const dismissNotification = (notification, isGSG) => {
        const now = new Date().getTime();
        const notificationKey = isGSG
            ? generateNotificationKeyGSG(notification)
            : generateNotificationKeyCMS(notification);
        const updatedDismissed = { ...dismissed, [notificationKey]: now };
        // console.log("Dismissed: ", updatedDismissed);

        setDismissed(updatedDismissed);
        localStorage.setItem("dismissedNotifications", JSON.stringify(updatedDismissed));
    };

    const getVisibleNotifications = () => {
        const dismissedKeys = Object.keys(dismissed);

        const filterNotifications = (notifications, keyGenerator) => {
            if (!Array.isArray(notifications)) {
                console.error("Invalid notifications input: expected an array but received", notifications);
                return [];
            }

            return notifications.filter(notification => {
                const notificationKey = keyGenerator(notification);
                return !dismissedKeys.includes(notificationKey);
            });
        };

        const visibleGSG = Object.keys(notifications.gsg || {}).reduce((result, siteKey) => {
            const gsgNotifications = notifications.gsg[siteKey];
            result[siteKey] = filterNotifications(gsgNotifications || [], generateNotificationKeyGSG);
            return result;
        }, {});
        const visibleGlobal = filterNotifications(notifications.global || [], generateNotificationKeyCMS);
        const visibleDivisions = Object.keys(notifications.division || {}).reduce((result, divisionKey) => {
            const divisionNotifications = notifications.division[divisionKey];
            result[divisionKey] = filterNotifications(
                divisionNotifications || [],
                generateNotificationKeyCMS
            );
            return result;
        }, {});

        const visibleNotifications = {
            gsg: visibleGSG,
            global: visibleGlobal,
            division: visibleDivisions
        };

        // console.log("Visible Notifications: ", visibleNotifications);
        return visibleNotifications;
    };

    useEffect(() => {
        const initializeNotifications = async () => {
            loadDismissed();
            await fetchGSGBanners();
            await fetchGlobalAlerts();
            await fetchDivisionAlerts();
        };

        initializeNotifications();
    }, []);

    return (
        <NotificationsContext.Provider
            value={{
                visibleNotifications: getVisibleNotifications(),
                dismissNotification
            }}>
            {children}
        </NotificationsContext.Provider>
    );
};

NotificationsProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useNotificationsContext = () => useContext(NotificationsContext);
